"use client";

import type { VideoDubbingCardsBlock as VideoDubbingCardsBlockPayloadType } from "@mw/shared/payload-types";

import { Button } from "@ui/components/core/Button";
import { PreviewPlayerButton } from "@ui/components/core/PreviewPlayer/PreviewPlayerButton";
import { PreviewPlayerProgressRing } from "@ui/components/core/PreviewPlayer/PreviewPlayerProgressRing";
import { ScrollGrid } from "@ui/components/layout/ScrollGrid";
import clsx from "clsx";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { DubbingTrackChevron } from "../_videoDubbing/DubbingTrackChevron";
import { VIDEO_BOTTOM_BLUR_STYLE, VIDEO_BOTTOM_GRADIENT_STYLE } from "../_videoDubbing/constants";
import { useDubbedVideoPlayer } from "../_videoDubbing/useDubbedVideoPlayer";

type VideoDubbingCardsBlockPayloadItem = NonNullable<NonNullable<VideoDubbingCardsBlockPayloadType["items"]>[number]>;
type VideoDubbingCardsTextStyle = NonNullable<VideoDubbingCardsBlockPayloadType["textStyle"]>;

interface VideoDubbingCardsBlockItem extends Pick<VideoDubbingCardsBlockPayloadItem, "id" | "subtitle" | "title"> {
  dubbedAudioSrc: string;
  dubbedLabel: string;
  originalAudioSrc: string;
  originalLabel: string;
  videoSrc: string;
}

export interface VideoDubbingCardsBlockProps {
  className?: string;
  items: VideoDubbingCardsBlockItem[];
  textStyle?: VideoDubbingCardsTextStyle | null;
}

const ORIGINAL_TRACK_KEY = "original";
const DUBBED_TRACK_KEY = "dubbed";
const PRELOAD_TRACK_KEYS = [ORIGINAL_TRACK_KEY, DUBBED_TRACK_KEY] as const;
type TrackKey = (typeof PRELOAD_TRACK_KEYS)[number];

export function VideoDubbingCardsBlock({ className, items, textStyle = "smallHeadings" }: VideoDubbingCardsBlockProps) {
  const [activePlayerId, setActivePlayerId] = useState<string | null>(null);
  const resolvedTextStyle = textStyle === "largeHeadings" ? "largeHeadings" : "smallHeadings";

  if (items.length < 3) return null;

  return (
    <ScrollGrid
      className={className}
      items={items}
      renderItem={(item, index) => {
        const playerId = item.id ?? `${item.videoSrc}-${index}`;
        const hasCaption = Boolean(item.title || item.subtitle);

        return (
          <figure className='tw-flex tw-h-full tw-flex-col'>
            <VideoDubbingCardPlayer
              isActivePlayer={activePlayerId === playerId}
              item={item}
              onRequestPlayback={() => setActivePlayerId(playerId)}
            />
            {hasCaption && (
              <figcaption className='tw-mt-6 tw-px-2 sm:tw-px-4'>
                {item.title && (
                  <h3
                    className={clsx(
                      resolvedTextStyle === "smallHeadings"
                        ? "tw-type-sm tw-font-medium tw-text-gray-500"
                        : "tw-type-xl tw-text-black",
                    )}
                  >
                    {item.title}
                  </h3>
                )}
                {item.subtitle && (
                  <p
                    className={clsx(
                      "tw-type-sm tw-text-pretty",
                      item.title && "tw-mt-4",
                      resolvedTextStyle === "smallHeadings" ? "tw-text-black" : "tw-text-gray-500",
                    )}
                  >
                    {item.subtitle}
                  </p>
                )}
              </figcaption>
            )}
          </figure>
        );
      }}
    />
  );
}

function VideoDubbingCardPlayer({
  isActivePlayer,
  item,
  onRequestPlayback,
}: {
  isActivePlayer: boolean;
  item: VideoDubbingCardsBlockItem;
  onRequestPlayback: () => void;
}) {
  const t = useTranslations("ClientKeys.Globals");
  const {
    activeTrackKey,
    isPlaying,
    pausePlayback,
    progressAnimationStartedAt,
    registerAudioElement,
    selectTrack,
    togglePlayback,
    videoCurrentTime,
    videoDuration,
    videoEventHandlers,
    videoMotionStyle,
    videoRef,
  } = useDubbedVideoPlayer({
    initialTrackKey: ORIGINAL_TRACK_KEY,
    onBeforePlay: onRequestPlayback,
    preloadTrackKeys: PRELOAD_TRACK_KEYS,
  });
  const languageLabel = item.dubbedLabel;
  const originalLabel = item.originalLabel;
  const videoLabel = item.title || item.dubbedLabel;
  const buttonLabel = isPlaying ? t("pause") : t("play");
  const isOriginalTrackActive = activeTrackKey === ORIGINAL_TRACK_KEY;

  useEffect(() => {
    if (isActivePlayer) return;

    pausePlayback();
  }, [isActivePlayer, pausePlayback]);

  function handleTrackSelect(trackKey: TrackKey) {
    selectTrack(trackKey);
  }

  return (
    <div className='tw-group/dubbing-card tw-relative tw-isolate tw-aspect-square tw-overflow-hidden tw-rounded-2.5xl tw-bg-white'>
      <m.div className='tw-size-full tw-origin-center tw-overflow-hidden tw-transform-gpu' style={videoMotionStyle}>
        <video
          aria-label={videoLabel}
          className='tw-size-full tw-object-cover'
          muted
          playsInline
          preload='metadata'
          ref={videoRef}
          src={item.videoSrc}
          {...videoEventHandlers}
        />
      </m.div>
      <audio
        aria-hidden='true'
        preload='auto'
        ref={audioElement => registerAudioElement(ORIGINAL_TRACK_KEY, audioElement)}
        src={item.originalAudioSrc}
      />
      <audio
        aria-hidden='true'
        preload='auto'
        ref={audioElement => registerAudioElement(DUBBED_TRACK_KEY, audioElement)}
        src={item.dubbedAudioSrc}
      />
      <div
        aria-hidden='true'
        className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-14 tw-overflow-hidden tw-rounded-b-2.5xl tw-backdrop-blur-[2px]'
        style={VIDEO_BOTTOM_BLUR_STYLE}
      />
      <div
        aria-hidden='true'
        className='tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-28'
        style={VIDEO_BOTTOM_GRADIENT_STYLE}
      />
      <>
        <button
          type='button'
          aria-label={buttonLabel}
          aria-pressed={isPlaying}
          title={buttonLabel}
          className='tw-group/preview-button tw-absolute tw-left-1/2 tw-top-1/2 tw-z-10 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-full tw-bg-transparent tw-p-0 tw-opacity-100 tw-outline-none tw-transition-opacity tw-duration-300 tw-ease-out focus-visible:tw-outline-focus md:tw-opacity-0 md:group-focus-within/dubbing-card:tw-opacity-100 md:group-hover/dubbing-card:tw-opacity-100'
          onClick={togglePlayback}
        >
          <PreviewPlayerButton isActive={isPlaying} size='md'>
            <PreviewPlayerProgressRing
              currentTime={videoCurrentTime}
              duration={videoDuration}
              isActive={isPlaying}
              progressAnimationStartedAt={progressAnimationStartedAt}
              size='md'
            />
          </PreviewPlayerButton>
        </button>
        <div className='tw-absolute tw-inset-x-3 tw-bottom-4 tw-z-10 tw-flex tw-items-center tw-justify-center tw-gap-1.5'>
          <Button
            type='button'
            active={isOriginalTrackActive}
            activePill
            aria-pressed={isOriginalTrackActive}
            size='md-compact'
            variant='ghost-inverse'
            onClick={() => handleTrackSelect(ORIGINAL_TRACK_KEY)}
          >
            {originalLabel}
          </Button>
          <DubbingTrackChevron
            isLanguageTrackActive={!isOriginalTrackActive}
            label={isOriginalTrackActive ? languageLabel : originalLabel}
            onClick={() => handleTrackSelect(isOriginalTrackActive ? DUBBED_TRACK_KEY : ORIGINAL_TRACK_KEY)}
          />
          <Button
            type='button'
            active={!isOriginalTrackActive}
            activePill
            aria-pressed={!isOriginalTrackActive}
            size='md-compact'
            variant='ghost-inverse'
            onClick={() => handleTrackSelect(DUBBED_TRACK_KEY)}
          >
            {languageLabel}
          </Button>
        </div>
      </>
    </div>
  );
}
